import { Injectable } from '@angular/core';
import { ApiEntityService } from '@core/api/api-entity.service';
import { ApiService } from '@core/api/api.service';
import { Automapper, Mapper } from '@core/api/mapper';
import { envelopeKey, unwrapFromEnvelope } from '@core/api/entity-keys';
import { UserNamespacedResourceBaseUrlBuilder } from '@core/user-namespaced-resource-base-url-builder.service';
import { deserialize } from '@junte/serialize-ts';

import {
  PortfolioSimulationParameters,
  PortfolioSimulationPortfolio,
  PortfolioGraphRequest,
  PortfolioGraphResponse,
  PortfolioGraphsResponse,
  PortfolioGraphsPostResponse,
} from '@core/models/portfolio-simulation.model';

import { Observable } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { PortfolioSimulationState, PortfolioSimulationStore } from './portfolio-simulation.store';

@Injectable({ providedIn: 'root' })
export class PortfolioSimulationService extends ApiEntityService<PortfolioSimulationState> {
  private portfolioMapper: Mapper<PortfolioSimulationPortfolio, any>;
  // Store the last partnerUid used for fetchPortfolios to use in refresh calls after create/update/delete
  private lastPartnerUid?: string;

  constructor(
    store: PortfolioSimulationStore,
    urlBuilder: UserNamespacedResourceBaseUrlBuilder,
    private apiService: ApiService,
  ) {
    super(store, urlBuilder, Automapper.create(PortfolioSimulationPortfolio));
    this.portfolioMapper = Automapper.create(PortfolioSimulationPortfolio);
    this.overrideResourceName('common/portfolio-simulation/portfolios');
  }

  /**
   * Fetch portfolio simulation parameters
   * @param partnerUid Optional partner UID for superusers to query specific partner's parameters
   */
  fetchParameters(partnerUid?: string): Observable<PortfolioSimulationParameters> {
    (this.store as PortfolioSimulationStore).setLoading(true);
    let url = 'common/portfolio-simulation/parameters';
    if (partnerUid) {
      url += `?partner=${partnerUid}`;
    }

    return this.apiService.get<PortfolioSimulationParameters>(url).pipe(
      map((data) => deserialize(data, PortfolioSimulationParameters)),
      tap({
        next: (parameters) => {
          (this.store as PortfolioSimulationStore).setParameters(parameters);
          (this.store as PortfolioSimulationStore).setLoading(false);
        },
        error: () => (this.store as PortfolioSimulationStore).setLoading(false),
      }),
    );
  }

  /**
   * Update portfolio simulation parameters
   * @param parameters The parameters to update
   * @param partnerUid Optional partner UID for superusers to update specific partner's parameters
   */
  updateParameters(
    parameters: Partial<PortfolioSimulationParameters>,
    partnerUid?: string,
  ): Observable<PortfolioSimulationParameters> {
    (this.store as PortfolioSimulationStore).setLoading(true);
    let url = 'common/portfolio-simulation/parameters';
    if (partnerUid) {
      url += `?partner=${partnerUid}`;
    }

    return this.apiService.patch<PortfolioSimulationParameters>(url, parameters).pipe(
      map((data) => deserialize(data, PortfolioSimulationParameters)),
      tap({
        next: (updatedParameters) => {
          (this.store as PortfolioSimulationStore).setParameters(updatedParameters);
          (this.store as PortfolioSimulationStore).setLoading(false);
        },
        error: () => (this.store as PortfolioSimulationStore).setLoading(false),
      }),
    );
  }

  /**
   * Fetch all portfolios for the current partner
   * @param partnerUid Optional partner UID for superusers to query specific partner's portfolios
   */
  fetchPortfolios(partnerUid?: string): Observable<PortfolioSimulationPortfolio[]> {
    (this.store as PortfolioSimulationStore).setLoading(true);
    // Store partnerUid for use in refresh calls after create/update/delete
    if (partnerUid !== undefined) {
      this.lastPartnerUid = partnerUid;
    }
    let url = 'common/portfolio-simulation/portfolios';
    if (partnerUid) {
      url += `?partner=${partnerUid}`;
    }

    return this.apiService.get<{ results: PortfolioSimulationPortfolio[] }>(url).pipe(
      map((response) => {
        // The interceptor may unwrap, but we handle both cases
        let results: any[];
        if (Array.isArray(response)) {
          // Already unwrapped by interceptor
          results = response;
        } else if (response && typeof response === 'object' && 'results' in response) {
          // Still wrapped in { results: [...] }
          results = (response as any).results || [];
        } else {
          // Fallback to unwrapFromEnvelope
          results = unwrapFromEnvelope(response, envelopeKey) || [];
        }

        if (!results || results.length === 0) {
          return [];
        }

        // Convert string values to numbers after deserialization
        const toNumber = (value: any): number => {
          if (value == null || value === undefined) return 0;
          if (typeof value === 'string') {
            const num = parseFloat(value);
            return isNaN(num) ? 0 : num;
          }
          return Number(value) || 0;
        };

        return results.map((item: any) => {
          const portfolio = this.portfolioMapper.toModel(item);
          // Ensure id is set correctly
          if (!portfolio.id && item.uid) {
            (portfolio as any).id = item.uid;
          }
          // Ensure percentage values are numbers
          portfolio.stocksPercentage = toNumber(portfolio.stocksPercentage);
          portfolio.bondsPercentage = toNumber(portfolio.bondsPercentage);
          portfolio.privateMarketsPercentage = toNumber(portfolio.privateMarketsPercentage);
          return portfolio;
        });
      }),
      tap({
        next: (portfolios) => {
          this.store.remove();
          this.store.set(portfolios);
          (this.store as PortfolioSimulationStore).setLoading(false);
        },
        error: () => (this.store as PortfolioSimulationStore).setLoading(false),
      }),
    );
  }

  /**
   * Create a new portfolio
   * @param portfolio The portfolio data to create
   * @param partnerUid Optional partner UID for superusers to create portfolio for specific partner
   */
  createPortfolio(
    portfolio: Partial<PortfolioSimulationPortfolio>,
    partnerUid?: string,
  ): Observable<PortfolioSimulationPortfolio> {
    (this.store as PortfolioSimulationStore).setLoading(true);
    // Store partnerUid for use in refresh calls after create
    if (partnerUid !== undefined) {
      this.lastPartnerUid = partnerUid;
    }
    let url = 'common/portfolio-simulation/portfolios';
    if (partnerUid) {
      url += `?partner=${partnerUid}`;
    }
    // Format percentages as strings with 2 decimal places (backend expects DecimalField format)
    const dto: any = {
      modelName: portfolio.modelName,
      stocksPercentage: portfolio.stocksPercentage != null ? portfolio.stocksPercentage.toFixed(2) : undefined,
      bondsPercentage: portfolio.bondsPercentage != null ? portfolio.bondsPercentage.toFixed(2) : undefined,
      privateMarketsPercentage:
        portfolio.privateMarketsPercentage != null ? portfolio.privateMarketsPercentage.toFixed(2) : undefined,
    };

    // Include isDefault if provided
    if (portfolio.isDefault !== undefined) {
      dto.isDefault = portfolio.isDefault;
    }

    return this.apiService.post<PortfolioSimulationPortfolio>(url, dto).pipe(
      map((data) => {
        const portfolio = this.portfolioMapper.toModel(data);
        // Ensure id is set correctly (map uid to id if needed)
        if (!portfolio.id && (data as any).uid) {
          (portfolio as any).id = (data as any).uid;
        }
        // Ensure percentage values are numbers (API returns strings)
        const toNumber = (value: any): number => {
          if (value == null || value === undefined) return 0;
          if (typeof value === 'string') {
            const num = parseFloat(value);
            return isNaN(num) ? 0 : num;
          }
          return Number(value) || 0;
        };
        portfolio.stocksPercentage = toNumber(portfolio.stocksPercentage);
        portfolio.bondsPercentage = toNumber(portfolio.bondsPercentage);
        portfolio.privateMarketsPercentage = toNumber(portfolio.privateMarketsPercentage);
        return portfolio;
      }),
      tap({
        next: () => {
          // Refresh the entire list to ensure data consistency
          // Use the last partnerUid if it was set (for superusers viewing specific partner)
          // Only pass partnerUid if it was explicitly set to maintain partner context
          const partnerUidToUse = this.lastPartnerUid !== undefined ? this.lastPartnerUid : undefined;
          this.fetchPortfolios(partnerUidToUse).subscribe();
        },
        error: () => (this.store as PortfolioSimulationStore).setLoading(false),
      }),
    );
  }

  /**
   * Update an existing portfolio
   * @param portfolioId The portfolio ID to update
   * @param portfolio The portfolio data to update
   * @param partnerUid Optional partner UID for superusers to update specific partner's portfolio
   */
  updatePortfolio(
    portfolioId: string,
    portfolio: Partial<PortfolioSimulationPortfolio>,
    partnerUid?: string,
  ): Observable<PortfolioSimulationPortfolio> {
    (this.store as PortfolioSimulationStore).setLoading(true);
    // Store partnerUid for use in refresh calls after update
    if (partnerUid !== undefined) {
      this.lastPartnerUid = partnerUid;
    }
    let url = `common/portfolio-simulation/portfolios/${portfolioId}`;
    if (partnerUid) {
      url += `?partner=${partnerUid}`;
    }
    // Format percentages as strings with 2 decimal places (backend expects DecimalField format)
    const dto: any = {};
    if (portfolio.modelName !== undefined) dto.modelName = portfolio.modelName;
    if (portfolio.stocksPercentage !== undefined) dto.stocksPercentage = portfolio.stocksPercentage.toFixed(2);
    if (portfolio.bondsPercentage !== undefined) dto.bondsPercentage = portfolio.bondsPercentage.toFixed(2);
    if (portfolio.privateMarketsPercentage !== undefined)
      dto.privateMarketsPercentage = portfolio.privateMarketsPercentage.toFixed(2);
    if (portfolio.isDefault !== undefined) dto.isDefault = portfolio.isDefault;

    return this.apiService.patch<PortfolioSimulationPortfolio>(url, dto).pipe(
      map((data) => {
        const portfolio = this.portfolioMapper.toModel(data);
        // Ensure id is set correctly (map uid to id if needed)
        if (!portfolio.id && (data as any).uid) {
          (portfolio as any).id = (data as any).uid;
        }
        // Ensure percentage values are numbers (API returns strings)
        const toNumber = (value: any): number => {
          if (value == null || value === undefined) return 0;
          if (typeof value === 'string') {
            const num = parseFloat(value);
            return isNaN(num) ? 0 : num;
          }
          return Number(value) || 0;
        };
        portfolio.stocksPercentage = toNumber(portfolio.stocksPercentage);
        portfolio.bondsPercentage = toNumber(portfolio.bondsPercentage);
        portfolio.privateMarketsPercentage = toNumber(portfolio.privateMarketsPercentage);
        return portfolio;
      }),
      tap({
        next: () => {
          // Refresh the entire list to ensure data consistency
          // Use the last partnerUid if it was set (for superusers viewing specific partner)
          // Only pass partnerUid if it was explicitly set to maintain partner context
          const partnerUidToUse = this.lastPartnerUid !== undefined ? this.lastPartnerUid : undefined;
          this.fetchPortfolios(partnerUidToUse).subscribe();
        },
        error: () => (this.store as PortfolioSimulationStore).setLoading(false),
      }),
    );
  }

  /**
   * Delete a portfolio
   */
  deletePortfolio(portfolioId: string): Observable<void> {
    (this.store as PortfolioSimulationStore).setLoading(true);
    const url = `common/portfolio-simulation/portfolios/${portfolioId}`;

    return this.apiService.delete<void>(url).pipe(
      tap({
        next: () => {
          // Refresh the entire list to ensure data consistency
          // Use the last partnerUid if it was set (for superusers viewing specific partner)
          // Only pass partnerUid if it was explicitly set to maintain partner context
          const partnerUidToUse = this.lastPartnerUid !== undefined ? this.lastPartnerUid : undefined;
          this.fetchPortfolios(partnerUidToUse).subscribe();
        },
        error: () => (this.store as PortfolioSimulationStore).setLoading(false),
      }),
    );
  }

  /**
   * Recalculate portfolio metrics
   */
  recalculatePortfolio(portfolioId?: string): Observable<{ message: string; portfolio_uid?: string; count?: number }> {
    (this.store as PortfolioSimulationStore).setLoading(true);
    const url = 'common/portfolio-simulation/recalculate';
    const body = portfolioId ? { portfolio_uid: portfolioId } : {};

    return this.apiService.post<{ message: string; portfolio_uid?: string; count?: number }>(url, body).pipe(
      tap({
        next: () => {
          (this.store as PortfolioSimulationStore).setLoading(false);
          // Optionally refresh portfolio after recalculation
          if (portfolioId) {
            const getUrl = `common/portfolio-simulation/portfolios/${portfolioId}`;
            this.apiService.get<PortfolioSimulationPortfolio>(getUrl).subscribe((data) => {
              const portfolio = this.portfolioMapper.toModel(data);
              this.store.update(portfolio.id, portfolio);
            });
          }
        },
        error: () => (this.store as PortfolioSimulationStore).setLoading(false),
      }),
    );
  }

  /**
   * Fetch graph/chart data for portfolio visualization
   * POST /graphs returns PortfolioGraphsPostResponse with portfolios array
   * @param request The graph request data
   * @param partnerUid Optional partner UID for superusers to query specific partner's graphs
   */
  fetchGraphData(request: PortfolioGraphRequest, partnerUid?: string): Observable<PortfolioGraphsPostResponse> {
    (this.store as PortfolioSimulationStore).setLoading(true);
    let url = 'common/portfolio-simulation/graphs';
    if (partnerUid) {
      url += `?partner=${partnerUid}`;
    }

    return this.apiService.post<PortfolioGraphsPostResponse>(url, request).pipe(
      tap({
        next: () => {
          (this.store as PortfolioSimulationStore).setLoading(false);
        },
        error: () => (this.store as PortfolioSimulationStore).setLoading(false),
      }),
    );
  }

  /**
   * Fetch all portfolios with graph data (GET version - for initial load)
   * GET /api/common/portfolio-simulation/graphs (no body)
   * Returns all portfolios with their default allocations and chart data
   * @param partnerUid Optional partner UID for superusers to query specific partner's graphs
   */
  fetchAllPortfoliosWithGraphs(partnerUid?: string): Observable<PortfolioGraphsResponse> {
    (this.store as PortfolioSimulationStore).setLoading(true);
    let url = 'common/portfolio-simulation/graphs';
    if (partnerUid) {
      url += `?partner=${partnerUid}`;
    }

    return this.apiService.get<PortfolioGraphsResponse>(url).pipe(
      tap({
        next: () => {
          (this.store as PortfolioSimulationStore).setLoading(false);
        },
        error: () => (this.store as PortfolioSimulationStore).setLoading(false),
      }),
    );
  }

  /**
   * Fetch graph data for a specific portfolio by UID (for View Details)
   * GET /api/common/portfolio-simulation/graphs/{uid}
   * @param portfolioUid The portfolio UID
   * @param partnerUid Optional partner UID for superusers to query specific partner's portfolio detail
   */
  fetchGraphDataByPortfolioUid(portfolioUid: string, partnerUid?: string): Observable<PortfolioGraphResponse> {
    (this.store as PortfolioSimulationStore).setLoading(true);
    let url = `common/portfolio-simulation/graphs/${portfolioUid}`;
    if (partnerUid) {
      url += `?partner=${partnerUid}`;
    }

    return this.apiService.get<PortfolioGraphResponse>(url).pipe(
      tap({
        next: () => {
          (this.store as PortfolioSimulationStore).setLoading(false);
        },
        error: () => (this.store as PortfolioSimulationStore).setLoading(false),
      }),
    );
  }
}
